import { useMemo } from "react"
import { ProtectedRoute } from "@/lib/auth/protected-route"
import { useSearchParamState } from "@/lib/utils/use-search-param-state"
import { AppLayout } from "@/components/layout/app-layout"
import {
  DownloadCloud01 as Download,
  CurrencyDollar as DollarSign,
  FileCheck02 as FileText,
  TrendUp01 as TrendingUp,
  Users01 as Users,
  Building01 as Building2,
  UserCircle as Briefcase,
} from "@untitledui/icons"
import { useAuth } from "@/lib/auth/auth-context"
import { Roles } from "@/lib/team/roles"
import { useCurrentRole } from "@/lib/team/use-current-role"
import { useOrganization } from "@/lib/organization/organization-context"
import {
  useEstimatesReport,
  useDealershipEstimatesReports,
} from "@/lib/api/use-reporting"
import type { EstimatesReportPerUserRow } from "@/lib/api/types"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts"
import { motion } from "motion/react"

// ─── tiny date helpers (kept here to avoid pulling in date-fns) ──────────────

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const
const MONTH_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"] as const

// All boundaries are computed in UTC so that the ISO strings the BE filters
// against don't drift by the viewer's timezone offset (e.g. a user in
// America/Los_Angeles starting "this week" at local midnight Sun would have
// otherwise sent a Sun-08:00Z lower bound and clipped Sat-evening estimates).
function formatDateLabel(date: Date, fmt: "MMM dd, yyyy" | "MMMM yyyy") {
  if (fmt === "MMM dd, yyyy") {
    return `${MONTH_SHORT[date.getUTCMonth()]} ${String(date.getUTCDate()).padStart(2, "0")}, ${date.getUTCFullYear()}`
  }
  return `${MONTH_LONG[date.getUTCMonth()]} ${date.getUTCFullYear()}`
}

function subMonthsUTC(date: Date, months: number) {
  const result = new Date(date)
  result.setUTCMonth(result.getUTCMonth() - months)
  return result
}

function startOfWeekUTC(date: Date) {
  const result = new Date(date)
  result.setUTCDate(result.getUTCDate() - result.getUTCDay())
  result.setUTCHours(0, 0, 0, 0)
  return result
}

function endOfWeekUTC(date: Date) {
  const result = new Date(date)
  result.setUTCDate(result.getUTCDate() + (6 - result.getUTCDay()))
  result.setUTCHours(23, 59, 59, 999)
  return result
}

function startOfMonthUTC(date: Date) {
  const result = new Date(date)
  result.setUTCDate(1)
  result.setUTCHours(0, 0, 0, 0)
  return result
}

function endOfMonthUTC(date: Date) {
  const result = new Date(date)
  result.setUTCMonth(result.getUTCMonth() + 1)
  result.setUTCDate(0)
  result.setUTCHours(23, 59, 59, 999)
  return result
}

// ─── time period selector ────────────────────────────────────────────────────

type TimePeriod = "this-week" | "month" | "all-time"

function resolveDateRange(period: TimePeriod, monthsAgo: number): { startDate: Date | null; endDate: Date | null } {
  if (period === "all-time") return { startDate: null, endDate: null }
  if (period === "this-week") {
    const now = new Date()
    return { startDate: startOfWeekUTC(now), endDate: endOfWeekUTC(now) }
  }
  const target = subMonthsUTC(new Date(), monthsAgo)
  return { startDate: startOfMonthUTC(target), endDate: endOfMonthUTC(target) }
}

// ─── csv helpers ─────────────────────────────────────────────────────────────

function csvEscape(value: string | number) {
  const s = String(value)
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`
  return s
}

function downloadCsv(rows: (string | number)[][], filename: string) {
  const blob = new Blob([rows.map((r) => r.map(csvEscape).join(",")).join("\n")], { type: "text/csv" })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  a.click()
  window.URL.revokeObjectURL(url)
}

// ─── page ────────────────────────────────────────────────────────────────────

export default function ReportsPage() {
  const { user } = useAuth()
  const {
    activeDealershipId,
    activeGroup,
    groupDealerships,
    availableDealerships,
  } = useOrganization()
  const currentRole = useCurrentRole()

  // Director-level access ("see everything across the group") includes system
  // super-admins. General-Manager-level access is dealership-scoped.
  const isDirector = !!user?.admin || currentRole === Roles.Director

  const [timePeriodRaw, setTimePeriodRaw] = useSearchParamState("period", "this-week")
  const timePeriod: TimePeriod =
    timePeriodRaw === "month" || timePeriodRaw === "all-time" ? timePeriodRaw : "this-week"
  const setTimePeriod = (next: TimePeriod) => setTimePeriodRaw(next)
  const [selectedMonthRaw, setSelectedMonth] = useSearchParamState("month", "0")
  const selectedMonth = Number.parseInt(selectedMonthRaw) || 0
  const [performanceDealershipFilter, setPerformanceDealershipFilter] = useSearchParamState(
    "dealership",
    "all",
  )

  const { startDate, endDate } = useMemo(
    () => resolveDateRange(timePeriod, selectedMonth),
    [timePeriod, selectedMonth],
  )

  // Pick the team to scope the main report against:
  //  - Director with an active group → group id (rolls up across dealerships)
  //  - Otherwise → the active dealership
  const reportTeamId = isDirector && activeGroup ? activeGroup.id : activeDealershipId

  const { data: report, isLoading: isLoadingReport } = useEstimatesReport(
    reportTeamId,
    startDate,
    endDate,
  )

  // Per-dealership reports — only fetched when a Director needs the breakdown
  // table. Each call hits /reporting/estimates/team/:dealershipId in parallel.
  const dealershipsForBreakdown = isDirector
    ? (groupDealerships.length > 0 ? groupDealerships : availableDealerships)
    : []
  const dealershipReports = useDealershipEstimatesReports(
    dealershipsForBreakdown,
    startDate,
    endDate,
    isDirector,
  )

  const filteredDealershipReports = useMemo(() => {
    if (performanceDealershipFilter === "all") return dealershipReports
    return dealershipReports.filter((d) => d.dealership.id === performanceDealershipFilter)
  }, [dealershipReports, performanceDealershipFilter])

  const dealershipTotals = useMemo(() => {
    return filteredDealershipReports.reduce(
      (acc, row) => {
        acc.totalEstimates += row.data?.totalEstimates ?? 0
        acc.totalRevenue += row.data?.totalRevenue ?? 0
        return acc
      },
      { totalEstimates: 0, totalRevenue: 0 },
    )
  }, [filteredDealershipReports])

  const advisorChartData = useMemo(
    () =>
      (report?.perServiceAdvisor ?? [])
        .filter((row) => row.totalRevenue > 0)
        .sort((a, b) => b.totalRevenue - a.totalRevenue)
        .slice(0, 10)
        .map((row) => ({
          name: row.name.split(" ")[0] || row.name,
          revenue: row.totalRevenue,
        })),
    [report],
  )

  const managerChartData = useMemo(
    () =>
      (report?.perServiceManager ?? [])
        .filter((row) => row.totalRevenue > 0)
        .sort((a, b) => b.totalRevenue - a.totalRevenue)
        .slice(0, 10)
        .map((row) => ({
          name: row.name.split(" ")[0] || row.name,
          revenue: row.totalRevenue,
        })),
    [report],
  )

  const handleExport = () => {
    if (isDirector) {
      const rows: (string | number)[][] = [
        ["Dealership", "Total Estimates", "Total Revenue", "Avg. Estimate", "Completion Rate (%)"],
      ]
      filteredDealershipReports.forEach(({ dealership, data }) => {
        rows.push([
          dealership.name,
          data?.totalEstimates ?? 0,
          data?.totalRevenue ?? 0,
          data?.avgValue ?? 0,
          data?.completionRate ?? "0.0",
        ])
      })
      const periodLabel = periodLabelForFilename(timePeriod, selectedMonth)
      downloadCsv(rows, `dealerships_${periodLabel}.csv`)
      return
    }

    const rows: (string | number)[][] = [
      ["Role", "Name", "Total Estimates", "Total Revenue", "Avg. Estimate", "Completion Rate (%)"],
    ]
    ;(report?.perServiceAdvisor ?? []).forEach((row) => {
      rows.push(["Service Advisor", row.name, row.totalEstimates, row.totalRevenue, row.avgRevenue, row.completionRate])
    })
    ;(report?.perServiceManager ?? []).forEach((row) => {
      rows.push(["Service Manager", row.name, row.totalEstimates, row.totalRevenue, row.avgRevenue, row.completionRate])
    })
    const periodLabel = periodLabelForFilename(timePeriod, selectedMonth)
    downloadCsv(rows, `report_${periodLabel}.csv`)
  }

  const completionRatePct = report ? Number.parseFloat(report.completionRate) : 0

  return (
    <ProtectedRoute>
      <AppLayout>
        <div className="flex-1 p-4 md:p-8 overflow-auto max-w-[1400px] mx-auto">
          {/* Header */}
          <div className="animate-element flex flex-col gap-3 md:flex-row md:items-start md:justify-between mb-6 md:mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-zinc-900 tracking-tight">
                Reports & Analytics
              </h1>
              <p className="text-sm text-zinc-400 mt-0.5 md:mt-1">
                {isDirector ? "Performance metrics across all dealerships" : "Performance metrics and trends"}
              </p>
            </div>
            <button
              onClick={handleExport}
              disabled={!report || isLoadingReport}
              className="h-11 px-5 rounded-xl border border-zinc-200 bg-zinc-50 text-sm font-medium text-zinc-700 flex items-center gap-2 hover:bg-white hover:border-zinc-300 transition-all duration-150 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="size-4" />
              Export CSV
            </button>
          </div>

          {/* Time Period Selector */}
          <div className="animate-element animate-delay-100 rounded-2xl border border-zinc-200/60 bg-white shadow-sm px-4 md:px-6 py-4 mb-6 md:mb-8">
            <div className="flex items-center gap-4 flex-wrap">
              <span className="text-sm font-medium text-zinc-500">Time Period:</span>
              <div className="flex items-center gap-2">
                {(["this-week", "all-time"] as const).map((period) => (
                  <button
                    key={period}
                    onClick={() => setTimePeriod(period)}
                    className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                      timePeriod === period
                        ? "bg-zinc-900 text-white shadow-sm"
                        : "text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50"
                    }`}
                  >
                    {period === "this-week" ? "This Week" : "All Time"}
                  </button>
                ))}
                <select
                  value={timePeriod === "month" ? selectedMonthRaw : ""}
                  onChange={(e) => {
                    setTimePeriod("month")
                    setSelectedMonth(e.target.value)
                  }}
                  className={`h-9 appearance-none rounded-lg border px-3 pr-8 text-sm outline-none cursor-pointer transition-all duration-150 ${
                    timePeriod === "month"
                      ? "border-zinc-900 bg-zinc-900 text-white"
                      : "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50"
                  }`}
                >
                  <option value="" disabled>Select Month</option>
                  {[0, 1, 2, 3, 4, 5].map((monthsAgo) => (
                    <option key={monthsAgo} value={monthsAgo.toString()}>
                      {formatDateLabel(subMonthsUTC(new Date(), monthsAgo), "MMMM yyyy")}
                    </option>
                  ))}
                </select>
              </div>
              <span className="ml-auto text-xs text-zinc-400">
                {timePeriod === "all-time" || !startDate || !endDate
                  ? "All Time"
                  : `${formatDateLabel(startDate, "MMM dd, yyyy")} — ${formatDateLabel(endDate, "MMM dd, yyyy")}`}
              </span>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="animate-element animate-delay-100 grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5 mb-6 md:mb-8">
            {[
              {
                label: "Total Revenue",
                value: `$${(report?.totalRevenue ?? 0).toLocaleString()}`,
                sub: isDirector ? "All dealerships" : "Current period",
                icon: DollarSign, iconBg: "bg-emerald-50", iconColor: "text-emerald-600", mono: true,
              },
              {
                label: "Total Estimates",
                value: (report?.totalEstimates ?? 0).toString(),
                sub: report ? `${report.completionRate}% completion rate` : undefined,
                icon: FileText, iconBg: "bg-blue-50", iconColor: "text-blue-600",
              },
              {
                label: "Average Estimate",
                value: `$${(report?.avgValue ?? 0).toLocaleString()}`,
                icon: TrendingUp, iconBg: "bg-violet-50", iconColor: "text-violet-600", mono: true,
              },
              {
                label: isDirector ? "Dealerships" : "Completion Rate",
                value: isDirector
                  ? dealershipsForBreakdown.length.toString()
                  : `${completionRatePct.toFixed(0)}%`,
                sub: isDirector ? "Active locations" : undefined,
                icon: isDirector ? Building2 : Users,
                iconBg: "bg-amber-50", iconColor: "text-amber-600",
                progress: !isDirector ? completionRatePct : undefined,
              },
            ].map((stat, i) => (
              <div
                key={stat.label}
                className="animate-element rounded-2xl border border-zinc-200/60 bg-white shadow-sm p-4 md:p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
                style={{ animationDelay: `${100 + i * 50}ms` }}
              >
                <div className="flex items-start justify-between mb-4">
                  <p className="text-sm font-medium text-zinc-500">{stat.label}</p>
                  <div className={`size-10 rounded-xl ${stat.iconBg} flex items-center justify-center`}>
                    <stat.icon className={`size-5 ${stat.iconColor}`} />
                  </div>
                </div>
                <p className={`text-2xl md:text-4xl font-bold text-zinc-900 tracking-tight ${stat.mono ? "font-mono" : ""}`}>
                  {stat.value}
                </p>
                {stat.sub && <p className="text-sm text-zinc-400 mt-1">{stat.sub}</p>}
                {stat.progress !== undefined && (
                  <div className="mt-3">
                    <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${stat.progress}%` }}
                        transition={{ duration: 0.8, delay: 0.3 }}
                        className="h-full bg-primary-500 rounded-full"
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Dealership Performance Table (Director only) */}
          {isDirector && dealershipsForBreakdown.length > 0 && (
            <div className="animate-element animate-delay-200 rounded-2xl border border-zinc-200/60 bg-white shadow-sm overflow-hidden mb-8">
              <div className="px-6 py-5 border-b border-zinc-100 flex items-center justify-between">
                <div>
                  <h3 className="text-base font-semibold text-zinc-900">Dealership Performance</h3>
                  <p className="text-sm text-zinc-400 mt-0.5">Per-location estimates and revenue</p>
                </div>
                <select
                  value={performanceDealershipFilter}
                  onChange={(e) => setPerformanceDealershipFilter(e.target.value)}
                  className="h-9 appearance-none rounded-xl border border-zinc-200 bg-zinc-50 px-4 pr-8 text-sm text-zinc-700 outline-none focus:border-zinc-400 focus:bg-white transition-all duration-150 cursor-pointer"
                >
                  <option value="all">All Dealerships</option>
                  {dealershipsForBreakdown.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-100 bg-zinc-50/50">
                      <th className="px-6 py-3.5 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">Dealership</th>
                      <th className="px-6 py-3.5 text-right text-xs font-semibold text-zinc-500 uppercase tracking-wider">Estimates</th>
                      <th className="px-6 py-3.5 text-right text-xs font-semibold text-zinc-500 uppercase tracking-wider">Total Revenue</th>
                      <th className="px-6 py-3.5 text-right text-xs font-semibold text-zinc-500 uppercase tracking-wider">Avg. Estimate</th>
                      <th className="px-6 py-3.5 text-right text-xs font-semibold text-zinc-500 uppercase tracking-wider">Completion</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-50">
                    {filteredDealershipReports.map(({ dealership, data, isLoading }, index) => (
                      <tr
                        key={dealership.id}
                        className={`hover:bg-zinc-50/80 transition-colors duration-100 ${index % 2 === 0 ? "bg-zinc-50/30" : ""}`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <div className="size-7 rounded-xl bg-zinc-100 flex items-center justify-center">
                              <Building2 className="size-3.5 text-primary-600" />
                            </div>
                            <span className="text-sm font-semibold text-zinc-900">{dealership.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-semibold text-zinc-900">
                          {isLoading ? "—" : data?.totalEstimates ?? 0}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-semibold text-zinc-900 font-mono">
                          {isLoading ? "—" : `$${(data?.totalRevenue ?? 0).toLocaleString()}`}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-semibold text-zinc-900 font-mono">
                          {isLoading ? "—" : `$${(data?.avgValue ?? 0).toLocaleString()}`}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-semibold text-zinc-900">
                          {isLoading ? "—" : `${data?.completionRate ?? "0.0"}%`}
                        </td>
                      </tr>
                    ))}
                    {filteredDealershipReports.length > 1 && performanceDealershipFilter === "all" && (
                      <tr className="bg-primary-50/30 border-t-2 border-primary-200">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2 text-sm font-bold text-zinc-900 uppercase tracking-wider">
                            <TrendingUp className="size-4" />
                            Total
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-bold text-zinc-900">{dealershipTotals.totalEstimates}</td>
                        <td className="px-6 py-4 text-right text-sm font-bold text-zinc-900 font-mono">${dealershipTotals.totalRevenue.toLocaleString()}</td>
                        <td className="px-6 py-4 text-right text-sm font-bold text-zinc-900 font-mono">
                          {dealershipTotals.totalEstimates > 0
                            ? `$${Math.ceil(dealershipTotals.totalRevenue / dealershipTotals.totalEstimates).toLocaleString()}`
                            : "—"}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-bold text-zinc-900">
                          {report?.completionRate ?? "0.0"}%
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Per-user breakdown charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <BreakdownChart
              title={isDirector ? "Top Service Advisors" : "Service Advisor Performance"}
              subtitle={isDirector ? "Revenue by advisor across all dealerships" : "Revenue by service advisor"}
              icon={Users}
              data={advisorChartData}
              rows={report?.perServiceAdvisor}
              isLoading={isLoadingReport}
              delayClass="animate-delay-200"
              emptyLabel="No advisor activity in this period"
            />
            <BreakdownChart
              title={isDirector ? "Top Service Managers" : "Service Manager Performance"}
              subtitle="Revenue by service / general manager"
              icon={Briefcase}
              data={managerChartData}
              rows={report?.perServiceManager}
              isLoading={isLoadingReport}
              delayClass="animate-delay-300"
              emptyLabel="No manager activity in this period"
            />
          </div>
        </div>
      </AppLayout>
    </ProtectedRoute>
  )
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function periodLabelForFilename(period: TimePeriod, monthsAgo: number) {
  if (period === "all-time") return "all-time"
  if (period === "this-week") return "this-week"
  const target = subMonthsUTC(new Date(), monthsAgo)
  return `${target.getUTCFullYear()}-${String(target.getUTCMonth() + 1).padStart(2, "0")}`
}

interface BreakdownChartProps {
  title: string
  subtitle: string
  icon: React.ComponentType<{ className?: string }>
  data: { name: string; revenue: number }[]
  rows?: EstimatesReportPerUserRow[]
  isLoading: boolean
  delayClass: string
  emptyLabel: string
}

function BreakdownChart({ title, subtitle, icon: Icon, data, rows, isLoading, delayClass, emptyLabel }: BreakdownChartProps) {
  const hasData = data.length > 0
  return (
    <div className={`animate-element ${delayClass} rounded-2xl border border-zinc-200/60 bg-white shadow-sm overflow-hidden`}>
      <div className="px-6 py-5 border-b border-zinc-100 flex items-center gap-3">
        <div className="size-9 rounded-xl bg-zinc-100 flex items-center justify-center">
          <Icon className="size-4 text-zinc-600" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-zinc-900">{title}</h3>
          <p className="text-xs text-zinc-400 mt-0.5">{subtitle}</p>
        </div>
      </div>
      <div className="p-6">
        {isLoading ? (
          <div className="h-[300px] flex items-center justify-center text-sm text-zinc-400">Loading…</div>
        ) : !hasData ? (
          <div className="h-[300px] flex items-center justify-center text-sm text-zinc-400">{emptyLabel}</div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data} layout="vertical" margin={{ left: 80, right: 20, top: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#a3a3a3" }} />
                <YAxis dataKey="name" type="category" width={75} tick={{ fontSize: 10, fill: "#a3a3a3" }} />
                <Tooltip
                  contentStyle={{ borderRadius: 12, border: "1px solid #e5e5e5", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }}
                  formatter={(value) => [`$${Number(value).toLocaleString()}`, "Revenue"]}
                />
                <Bar dataKey="revenue" fill="#1D9E75" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
            {rows && rows.length > 0 && (
              <div className="mt-4 overflow-x-auto -mx-6 px-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-wider text-zinc-400">
                      <th className="text-left py-2 font-medium">Name</th>
                      <th className="text-right py-2 font-medium">Estimates</th>
                      <th className="text-right py-2 font-medium">Revenue</th>
                      <th className="text-right py-2 font-medium">Avg.</th>
                      <th className="text-right py-2 font-medium">Completion</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-50">
                    {rows
                      .slice()
                      .sort((a, b) => b.totalRevenue - a.totalRevenue)
                      .slice(0, 10)
                      .map((row) => (
                        <tr key={row.userId}>
                          <td className="py-2 text-zinc-900">{row.name}</td>
                          <td className="py-2 text-right text-zinc-600">{row.totalEstimates}</td>
                          <td className="py-2 text-right text-zinc-900 font-mono">${row.totalRevenue.toLocaleString()}</td>
                          <td className="py-2 text-right text-zinc-600 font-mono">${row.avgRevenue.toLocaleString()}</td>
                          <td className="py-2 text-right text-zinc-600">{row.completionRate}%</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
